import { useDismissibleNotice } from "Shared/hooks/useDismissibleNotice"
import {
  BOOSTING_PHASE,
  BOOSTING_STEPS,
  BoostingStatus,
  BoostingStepKey,
  ONBOARDING_PHASE,
  ONBOARDING_STEPS,
  OnboardingStatus,
  OnboardingStepKey,
  PRACTICE_TEST_STATUS_MAP,
} from "UserCrowd/views/dashboard/actions-drawer/constants"
import { reportErrorToSentry } from "Utilities/error"
import {
  useCurrentPanelistInfo,
  useGetPanelistIntegrations,
  usePanelistStats,
} from "~/api/generated/usabilityhub-components"

export type OnboardingStep = {
  key: OnboardingStepKey
  phase: typeof ONBOARDING_PHASE
  status: OnboardingStatus
}

export type BoostingStep = {
  key: BoostingStepKey
  phase: typeof BOOSTING_PHASE
  status: BoostingStatus
}
interface ActionSteps {
  currentPhase: typeof ONBOARDING_PHASE | typeof BOOSTING_PHASE
  onboardingSteps: OnboardingStep[]
  boostingSteps: BoostingStep[]
}

export const useActionSteps = (): ActionSteps => {
  const { data: user, isLoading: isLoadingUser } = useCurrentPanelistInfo({})
  const { data: stats, isLoading: isLoadingStats } = usePanelistStats({})
  const {
    data: panelistIntegrations,
    isLoading: isLoadingPanelistIntegrations,
  } = useGetPanelistIntegrations({})
  const {
    isOpen: isOpenLinkedInVerificationCard,
    isLoading: isLoadingLinkedInVerificationCard,
  } = useDismissibleNotice("linkedin_verification_card")
  const { isOpen: isOpenProfileCard, isLoading: isLoadingProfileCard } =
    useDismissibleNotice("complete_profile_card")

  const onboardingSteps = [
    ...(ONBOARDING_STEPS.map((key) => {
      switch (key) {
        case "practice_test": {
          // Assume complete if loading, so that the step only appears when certain it is required and incomplete
          const practiceTestStatus = isLoadingUser
            ? "complete"
            : (PRACTICE_TEST_STATUS_MAP[
                user?.practice_test_state ?? "not_started"
              ] as OnboardingStatus)
          return {
            key,
            phase: ONBOARDING_PHASE,
            status: practiceTestStatus,
          }
        }
        default:
          reportErrorToSentry(new Error(`Unknown onboarding step key: ${key}`))
          return null
      }
    }).filter((step) => step !== null) as OnboardingStep[]),
  ]

  const boostingSteps = BOOSTING_STEPS.map((key) => {
    switch (key) {
      case "profile": {
        // Assume complete if loading, so that the step only appears when certain it is incomplete
        const profileStatus =
          isLoadingProfileCard ||
          isLoadingStats ||
          (stats && stats.profile_completeness >= 100)
            ? "complete"
            : isOpenProfileCard
              ? "incomplete"
              : ("dismissed" as BoostingStatus)
        return {
          key,
          phase: BOOSTING_PHASE,
          status: profileStatus,
        }
      }
      case "linkedin_verification": {
        const isVerified =
          !isLoadingPanelistIntegrations && !!panelistIntegrations?.linkedin
        const linkedInVerificationStatus = (
          isLoadingPanelistIntegrations ||
          isLoadingLinkedInVerificationCard ||
          isVerified
            ? "complete"
            : isOpenLinkedInVerificationCard
              ? "incomplete"
              : "dismissed"
        ) as BoostingStatus
        return {
          key,
          phase: BOOSTING_PHASE,
          status: linkedInVerificationStatus,
        }
      }
      default: {
        reportErrorToSentry(new Error(`Unknown boosting step key: ${key}`))
        return null
      }
    }
  }).filter((step) => step !== null) as BoostingStep[]

  const currentPhase = onboardingSteps.some(
    (step) => step.status === "incomplete" || step.status === "rejected"
  )
    ? ONBOARDING_PHASE
    : BOOSTING_PHASE

  return {
    currentPhase,
    onboardingSteps,
    boostingSteps,
  }
}
